import React, { useState, useEffect } from 'react';
import { useAuth } from '../../auth/AuthContext';
import { useRouter, Link } from '../../router/Router';
import { notificationService } from '../../services/notificationService';
import {
  Home,
  Ticket,
  PlusCircle,
  Bell,
  User,
  ShieldCheck,
  LogOut,
  ChevronRight,
  Compass,
} from 'lucide-react';
import './AppShell.css';

interface Props {
  children: React.ReactNode;
  title?: string;
  showBack?: boolean;
  backTo?: string;
}

export const AppShell: React.FC<Props> = ({
  children,
  title,
  showBack = false,
  backTo = '/student',
}) => {
  const { user, studentProfile, logout } = useAuth();
  const { currentPath, navigate } = useRouter();
  const [unreadNotifsCount, setUnreadNotifsCount] = useState(0);

  useEffect(() => {
    if (!user?.uid) return;
    const unsub = notificationService.subscribeToNotifications(user.uid, (items) => {
      const unread = items.filter((n) => !n.read).length;
      setUnreadNotifsCount(unread);
    });
    return () => unsub();
  }, [user?.uid]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  // Determine active tab
  const isHome = currentPath === '/student';
  const isBookings = currentPath === '/student/bookings' || currentPath.startsWith('/student/booking/');
  const isBookingFlow = currentPath === '/student/universities' || currentPath.startsWith('/student/trips');
  const isNotifications = currentPath === '/student/notifications';
  const isProfile = currentPath === '/student/profile';

  return (
    <div className="mobile-app-shell" dir="rtl">
      {/* 1. Header (Adaptive Mobile & Desktop) */}
      <header className="shell-header no-print">
        <div className="shell-header-inner">
          <div className="shell-header-start">
            {showBack ? (
              <button
                type="button"
                onClick={() => navigate(backTo)}
                className="shell-btn-back"
                aria-label="الرجوع"
              >
                <ChevronRight size={22} />
              </button>
            ) : (
              <Link to="/student" className="shell-brand-link">
                <span className="shell-brand-badge" style={{ padding: '0', overflow: 'hidden', width: '32px', height: '32px' }}>
                  <img src="/oto-logo.jpg" alt="OTO" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                </span>
                <span className="shell-brand-title">OTO STUDENTS</span>
              </Link>
            )}

            {title && <h2 className="shell-page-heading">{title}</h2>}
          </div>

          {/* Desktop Navigation Links */}
          <nav className="shell-desktop-nav" aria-label="التنقل الرئيسي">
            <Link to="/student" className={`desktop-nav-link ${isHome ? 'active' : ''}`}>
              <Home size={18} />
              <span>الرئيسية</span>
            </Link>
            <Link
              to="/student/universities"
              className={`desktop-nav-link ${isBookingFlow ? 'active' : ''}`}
            >
              <Compass size={18} />
              <span>حجز رحلة</span>
            </Link>
            <Link
              to="/student/bookings"
              className={`desktop-nav-link ${isBookings ? 'active' : ''}`}
            >
              <Ticket size={18} />
              <span>رحلاتي وتذاكري</span>
            </Link>
            <Link
              to="/student/notifications"
              className={`desktop-nav-link ${isNotifications ? 'active' : ''}`}
            >
              <Bell size={18} />
              <span>الإشعارات</span>
              {unreadNotifsCount > 0 && (
                <span className="nav-badge-pill">{unreadNotifsCount}</span>
              )}
            </Link>
            <Link to="/student/profile" className={`desktop-nav-link ${isProfile ? 'active' : ''}`}>
              <User size={18} />
              <span>الملف الشخصي</span>
            </Link>
          </nav>

          {/* User Controls */}
          <div className="shell-header-end">
            {(user?.email?.includes('admin') || (studentProfile as any)?.role === 'ADMIN') && (
              <Link to="/admin" className="shell-admin-btn" title="لوحة تحكم الإدارة">
                <ShieldCheck size={16} />
                <span className="desktop-only-text">الإدارة</span>
              </Link>
            )}

            <Link
              to="/student/notifications"
              className="shell-notif-btn"
              aria-label={`الإشعارات ${unreadNotifsCount > 0 ? `(${unreadNotifsCount} جديدة)` : ''}`}
            >
              <Bell size={20} />
              {unreadNotifsCount > 0 && (
                <span className="shell-notif-dot">{unreadNotifsCount}</span>
              )}
            </Link>

            <Link to="/student/profile" className="shell-avatar-link" title="الملف الشخصي">
              <div className="shell-avatar-circle">
                {studentProfile?.fullName ? studentProfile.fullName.charAt(0) : 'ط'}
              </div>
            </Link>

            <button
              type="button"
              onClick={handleLogout}
              className="shell-logout-btn desktop-only"
              title="تسجيل الخروج"
            >
              <LogOut size={16} />
            </button>
          </div>
        </div>
      </header>

      {/* 2. Main Scroll View */}
      <main className="shell-main-content">{children}</main>

      {/* 3. Mobile Bottom Navigation Bar (Fixed with Safe Area) */}
      <nav className="shell-bottom-nav no-print" aria-label="شريط التنقل السفلي">
        <div className="shell-bottom-nav-inner">
          <Link to="/student" className={`bottom-tab ${isHome ? 'active' : ''}`}>
            <div className="tab-icon-wrapper">
              <Home size={22} />
            </div>
            <span className="tab-label">الرئيسية</span>
          </Link>

          <Link to="/student/bookings" className={`bottom-tab ${isBookings ? 'active' : ''}`}>
            <div className="tab-icon-wrapper">
              <Ticket size={22} />
            </div>
            <span className="tab-label">رحلاتي</span>
          </Link>

          {/* Prominent Center Booking Button */}
          <Link
            to="/student/universities"
            className={`bottom-tab-center ${isBookingFlow ? 'active' : ''}`}
            aria-label="حجز رحلة جديدة"
          >
            <div className="center-action-btn">
              <PlusCircle size={26} />
            </div>
            <span className="tab-label">احجز</span>
          </Link>

          <Link
            to="/student/notifications"
            className={`bottom-tab ${isNotifications ? 'active' : ''}`}
          >
            <div className="tab-icon-wrapper">
              <Bell size={22} />
              {unreadNotifsCount > 0 && <span className="tab-badge">{unreadNotifsCount}</span>}
            </div>
            <span className="tab-label">الإشعارات</span>
          </Link>

          <Link to="/student/profile" className={`bottom-tab ${isProfile ? 'active' : ''}`}>
            <div className="tab-icon-wrapper">
              <User size={22} />
            </div>
            <span className="tab-label">حسابي</span>
          </Link>
        </div>
      </nav>
    </div>
  );
};
